import * as React from 'react'
import { Alert, Button, Spinner } from 'react-bootstrap'
import { useLocation, useNavigate } from 'react-router-dom'
import { BusinessForm, ContractStatus } from 'api/common/constants'
import { connectorContractAPI } from 'api/connector'
import Footer from 'components/Footer'
import Header from 'components/Header'
import InputFieldConfirm from 'components/InputFieldConfirm'
import loginData from 'utilities/loginData'
import { toHalfWidth } from 'utilities/moji'
import questions, { ConsentQuestions } from 'utilities/resources/consentQuestions'
import { connectSubscriptionFormItems } from 'utilities/resources/formItems'
import { ConnectSubscriptionFormValues } from 'utilities/resources/formItemValues'
import { getBankAccountTypeLabel, getGenderLabel, getPrefectureLabel, getYesNoLabel } from 'utilities/resources/options'
import { autoCast, autoCastOrUndefined, toNumber } from 'utilities/typeUtility'

type StateValue = {
  formValues?: ConnectSubscriptionFormValues
  consentQuestion?: ConsentQuestions
}

/** お申込み情報登録API失敗時（通信エラー含む）の汎用エラー文言 */
const REGISTER_ERROR_MESSAGE = '登録処理でエラーが発生しました。時間をおいて再度お試しください。' as const

const SceneComponent: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const stateValue = React.useMemo(() => (location.state ?? {}) as StateValue, [location])
  const formValues = stateValue.formValues
  const consentQuestion = stateValue.consentQuestion ?? {}
  const [committed, setCommitted] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState('')
  let bankAccountName = ''
  switch (loginData.getUserType()) {
    // 法人の場合
    case BusinessForm.Company:
      bankAccountName = toHalfWidth(formValues?.bankAccountName ?? '')
      break
    // 個人の場合　姓と名の間に半角スペースを入れる
    case BusinessForm.Individual:
      bankAccountName = toHalfWidth(`${formValues?.bankAccountLastName} ${formValues?.bankAccountFirstName}`)
      break
    default:
      break
  }

  /**
   * 「登録する」ボタン
   */
  const handleRegisterButton = () => {
    setCommitted(true)
    setErrorMessage('')
    connectorContractAPI(
      {
        countryCode: formValues?.countryNumber,
        tel: formValues?.phoneNumber,
        companyNameKana: formValues?.companyNameKana,
        companyInvoiceNumber: formValues?.companyInvoiceNumber,
        lastNameKana: formValues?.lastNameKana,
        firstNameKana: formValues?.firstNameKana,
        lastNameAlphabet: formValues?.lastNameAlphabet,
        firstNameAlphabet: formValues?.firstNameAlphabet,
        gender: autoCastOrUndefined(formValues?.gender),
        bankCode: formValues?.bankCode,
        bankName: formValues?.bankName,
        accountType: autoCastOrUndefined(formValues?.bankType),
        branchNumber: formValues?.bankBranchCode,
        branchName: formValues?.bankBranchName,
        accountNumber: formValues?.bankAccountNumber,
        accountName: bankAccountName,
        postCode: formValues?.postalCode,
        prefectureCode: toNumber(formValues?.prefecture),
        addressDetail: formValues?.address,
        postCodeForMail: formValues?.receivePostalCode,
        prefectureCodeForMail: toNumber(formValues?.receivePrefecture),
        addressDetailForMail: formValues?.receiveAddress,
        agreed: Object.values(consentQuestion).map((val) => autoCast(val)),
      },
      (result, contractStatus, userType) => {
        // 失敗時（通信エラー含む）は送信中フラグを解除して再操作可能にする
        if (!result) {
          setCommitted(false)
          setErrorMessage(REGISTER_ERROR_MESSAGE)
          return
        }
        switch (contractStatus) {
          case ContractStatus.ApplicationAndAgreementRegistered: {
            const path = userType === BusinessForm.Individual ? '/registerIdentificationForIndividual' : '/registerIdentificationForCompany'
            navigate(path)
            break
          }
          default: {
            navigate('/myPage')
            break
          }
        }
      },
    )
  }

  /**
   * 「入力内容を修正する」ボタン
   */
  const handleBackButton = () => {
    navigate('/connectorSubscription', { state: stateValue })
  }

  React.useEffect(() => {
    if (!loginData.isLoggedIn()) {
      navigate('/')
    } else if (!stateValue.formValues || !stateValue.consentQuestion) {
      navigate('/connectorSubscription')
    }
  }, [navigate, stateValue])
  return (
    <>
      <Header title="お申込み・承諾" />
      <main>
        <section className="mx-4 my-5">
          <p className="note">内容を確認のうえ、「お申込み」ボタンを押してください。</p>
          <div className="mt-5">
            <h3 className="fw-bold">お申込み情報</h3>
            <InputFieldConfirm label={connectSubscriptionFormItems.countryNumber.label} value={formValues?.countryNumber} bold />
            <InputFieldConfirm label={connectSubscriptionFormItems.phoneNumber.label} value={formValues?.phoneNumber} bold />
            {formValues?.businessForm === `${BusinessForm.Company}` && (
              <div>
                <InputFieldConfirm label={connectSubscriptionFormItems.companyNameKana.label} value={formValues.companyNameKana} bold />
                <InputFieldConfirm label={connectSubscriptionFormItems.companyInvoiceNumber.label} value={formValues.companyInvoiceNumber} bold />
              </div>
            )}
            <InputFieldConfirm label={connectSubscriptionFormItems.lastNameKana.label} value={formValues?.lastNameKana} bold />
            <InputFieldConfirm label={connectSubscriptionFormItems.firstNameKana.label} value={formValues?.firstNameKana} bold />
            <InputFieldConfirm label={connectSubscriptionFormItems.lastNameAlphabet.label} value={formValues?.lastNameAlphabet} bold />
            <InputFieldConfirm label={connectSubscriptionFormItems.firstNameAlphabet.label} value={formValues?.firstNameAlphabet} bold />
            <InputFieldConfirm label={connectSubscriptionFormItems.gender.label} value={getGenderLabel(formValues?.gender ?? '')} bold />
          </div>
          <div className="mt-5">
            <h3 className="fw-bold">報酬受取口座</h3>
            <InputFieldConfirm label={connectSubscriptionFormItems.bankCode.label} value={formValues?.bankCode} bold />
            <InputFieldConfirm label={connectSubscriptionFormItems.bankName.label} value={formValues?.bankName} bold />
            <InputFieldConfirm label={connectSubscriptionFormItems.bankType.label} value={getBankAccountTypeLabel(formValues?.bankType ?? '')} bold />
            <InputFieldConfirm label={connectSubscriptionFormItems.bankBranchCode.label} value={formValues?.bankBranchCode} bold />
            <InputFieldConfirm label={connectSubscriptionFormItems.bankBranchName.label} value={formValues?.bankBranchName} bold />
            <InputFieldConfirm label={connectSubscriptionFormItems.bankAccountNumber.label} value={formValues?.bankAccountNumber} bold />
            <InputFieldConfirm label={connectSubscriptionFormItems.bankAccountName?.label} value={bankAccountName} bold />
          </div>
          <div className="mt-5">
            <h3 className="fw-bold">{formValues?.businessForm === `${BusinessForm.Individual}` ? 'ご登録住所' : '本店所在地'}</h3>
            <InputFieldConfirm label={connectSubscriptionFormItems.postalCode.label} value={formValues?.postalCode} bold />
            <InputFieldConfirm label="住所" values={[getPrefectureLabel(formValues?.prefecture ?? ''), formValues?.address ?? '']} bold />
          </div>
          <div className="mt-5">
            <h3 className="fw-bold">郵送物受け取り住所</h3>
            <InputFieldConfirm label={connectSubscriptionFormItems.receivePostalCode.label} value={formValues?.receivePostalCode} bold />
            <InputFieldConfirm label="住所" values={[getPrefectureLabel(formValues?.receivePrefecture ?? ''), formValues?.receiveAddress ?? '']} bold />
          </div>
        </section>
        <section className="mx-4 my-5">
          <h3 className="fw-bold">AUBEクエストコネクタープランに関する承諾</h3>
          {questions.map(({ id, contents }, index) => (
            <InputFieldConfirm labels={contents} key={index} value={`▶ ${getYesNoLabel(consentQuestion[id] ?? '')}`} />
          ))}
        </section>
        <section className="mx-4 my-5">
          {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
          <div>
            <Button variant="primary" className="w-100 py-4" onClick={handleRegisterButton} disabled={committed}>
              <Spinner className="me-2" as="span" animation="border" size="sm" role="status" aria-hidden="true" hidden={!committed} />
              お申込み
            </Button>
          </div>
          <div className="mt-5 text-center">
            <Button variant="link" onClick={handleBackButton}>
              入力内容を修正する
            </Button>
          </div>
        </section>
      </main>
      <Footer className="mt-5" />
    </>
  )
}

export default SceneComponent
